import React, { useMemo } from 'react';
import { ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid, ResponsiveContainer } from 'recharts';
import { buildResponseTiming, describeCollection } from '../analysis/responseTiming';

// When answers arrived, by the moment each take FINISHED.
//
// Bars are how many arrived in each interval; the line is the running total, so
// an author can see both the shape of the response and how close to the final
// count they were on any given day.
export default function ResponsesOverTime({ responses = [] }) {
  const timing = useMemo(() => buildResponseTiming(responses), [responses]);

  if (timing.granularity === 'none') {
    return (
      <div role="status" style={{ padding: 40, textAlign: 'center', color: '#5F6368', fontFamily: 'var(--font-family-primary)' }}>
        No completed answers to time yet.
      </div>
    );
  }

  const unit = timing.granularity === 'hour' ? 'hour' : 'day';
  return (
    <div style={{ width: '100%', fontFamily: 'var(--font-family-primary)' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 12 }}>
        <Stat label="answers" value={timing.total} />
        <Stat label="first" value={timing.first.toLocaleString()} />
        <Stat label="last" value={timing.last.toLocaleString()} />
        <Stat label={`busiest ${unit}`} value={busiest(timing)} />
      </div>

      <ResponsiveContainer width="100%" height={340}>
        <ComposedChart data={timing.buckets} margin={{ top: 16, right: 56, left: 8, bottom: 56 }}>
          <CartesianGrid stroke="#F1F3F4" vertical={false} />
          <XAxis
            dataKey="label"
            interval="preserveStartEnd"
            angle={-30}
            textAnchor="end"
            height={56}
            tick={{ fill: '#5F6368', fontSize: 12 }}
          />
          <YAxis yAxisId="count" allowDecimals={false} tick={{ fill: '#5F6368', fontSize: 12 }} />
          <YAxis yAxisId="cumulative" orientation="right" allowDecimals={false} tick={{ fill: '#5F6368', fontSize: 12 }} />
          <Tooltip contentStyle={{ fontFamily: 'var(--font-family-primary)', fontSize: 12 }} />
          <Legend verticalAlign="top" align="left" wrapperStyle={{ fontSize: 12, paddingBottom: 8 }} />
          <Bar yAxisId="count" dataKey="count" name={`Answers that ${unit}`} fill="#1967D2" maxBarSize={48} isAnimationActive={false} />
          <Line yAxisId="cumulative" type="monotone" dataKey="cumulative" name="Running total" stroke="#0F9D58" strokeWidth={2} dot={false} isAnimationActive={false} />
        </ComposedChart>
      </ResponsiveContainer>

      <div style={{ fontSize: 12, color: '#5F6368', marginTop: 4 }}>
        {describeCollection(timing)} Timed by when each take finished, and every {unit} in the
        range is shown — a gap means nothing arrived.
      </div>
    </div>
  );
}

function busiest(timing) {
  const peak = timing.buckets.reduce((best, b) => (b.count > (best?.count ?? -1) ? b : best), null);
  return peak ? `${peak.label} (${peak.count})` : '—';
}

const Stat = ({ label, value }) => (
  <div style={{ display: 'flex', flexDirection: 'column' }}>
    <span style={{ fontSize: 11, color: '#5F6368' }}>{label}</span>
    <span style={{ fontSize: 13, color: '#202124', fontWeight: 600 }}>{value}</span>
  </div>
);
